import { QueryErrorResetBoundary } from "@tanstack/react-query";
import {
    Component,
    type ComponentType,
    type PropsWithChildren,
    type ReactNode,
    Suspense,
} from "react";

import type { ErrorScreenProps } from "../error/ErrorScreen";
import { AwaitConfig } from "./AwaitConfig";

type AwaitProps = PropsWithChildren<{
    loading?: ComponentType;
    error?: ComponentType<ErrorScreenProps>;
}>;

export default function Await(props: AwaitProps) {
    const { children } = props;
    const Loading = props.loading ?? AwaitConfig.loading;
    const ErrorScreen = props.error ?? AwaitConfig.error;
    return (
        <QueryErrorResetBoundary>
            {({ reset }) => (
                <ErrorBoundary
                    onReset={reset}
                    fallback={(error, retry) => <ErrorScreen error={error} onRetry={retry} />}
                >
                    <Suspense fallback={<Loading />}>{children}</Suspense>
                </ErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    );
}

type ErrorBoundaryProps = PropsWithChildren<{
    fallback: (error: unknown, reset: () => void) => ReactNode;
    onReset?: () => void;
}>;

type ErrorBoundaryState = {
    error: unknown | null;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    override state: ErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
        return { error };
    }

    private reset = () => {
        this.props.onReset?.();
        this.setState({ error: null });
    };

    override render() {
        const { error } = this.state;
        if (error) return this.props.fallback(error, this.reset);
        return this.props.children;
    }
}
